import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Link, Outlet } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: () => (
    <>
      <Outlet />
      <Toaster richColors position="top-right" />
    </>
  ),
  notFoundComponent: () => (
    <TelaAvulsa>
      <p className="kpi text-gradient-brand mt-6 text-6xl font-semibold leading-none">404</p>
      <h1 className="mt-4 font-display text-xl font-semibold tracking-tight sm:text-2xl">
        Página não encontrada
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        O endereço que você abriu não existe ou foi movido.
      </p>
      <div className="mt-6">
        <Button asChild>
          <Link to="/painel">Voltar ao painel</Link>
        </Button>
      </div>
    </TelaAvulsa>
  ),
  errorComponent: ({ error, reset }) => (
    <TelaAvulsa>
      <span className="mx-auto mt-6 inline-flex size-11 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
        <AlertTriangle className="size-5" />
      </span>
      <h1 className="mt-4 font-display text-xl font-semibold tracking-tight sm:text-2xl">
        Algo deu errado
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        A tela não conseguiu carregar. Tente de novo; se continuar, avise quem cuida do sistema.
      </p>
      {/* A mensagem técnica fica visível, mas discreta: é o que a pessoa vai
          copiar para pedir ajuda. */}
      {error.message && (
        <pre className="mt-4 overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-muted px-3 py-2 text-left font-mono text-xs text-muted-foreground">
          {error.message}
        </pre>
      )}
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Button onClick={reset}>Tentar de novo</Button>
        <Button asChild variant="outline">
          <Link to="/painel">Voltar ao painel</Link>
        </Button>
      </div>
    </TelaAvulsa>
  ),
});

/**
 * Tela sem shell (404 e erro): marca no topo e conteúdo centrado.
 *
 * `min-h-dvh` é permitido aqui porque estas telas substituem o app inteiro
 * — não há AppShell por fora para cuidar da altura.
 */
function TelaAvulsa({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4 py-10 sm:px-6">
      <div className="w-full max-w-md text-center animate-rise">
        <span
          aria-hidden
          className="bg-gradient-brand inline-flex size-12 items-center justify-center rounded-2xl font-display text-sm font-bold tracking-tight text-white shadow-panel"
        >
          RN
        </span>
        {children}
      </div>
    </div>
  );
}
